import { LINKS_PAGE_DEFAULT_LABELS, LinksPage } from './links-page';
import type { ComponentConfig } from '@puckeditor/core';
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { LinksPageFields } from "./links-page.fields";
import type { LinksPageProps } from './links-page';
import { getWindowJobsiteConfig } from '../../types/runtime-config';

/** The starting props of a fresh link page. Labels are seeded as real text
 *  (not blank-means-default) so the Label fields in the editor show what's on
 *  the buttons; rows resolve their targets from live business data. */
const LINKS_PAGE_DEFAULT_PROPS = {
    links: (["call", "book", "quote", "review", "directions", "gallery"] as const).map(
      (kind) => ({ kind, label: LINKS_PAGE_DEFAULT_LABELS[kind], url: "" }),
    ),
    logoUrl: "",
    photos: [],
    showRating: "yes" as const,
    showSocials: "yes" as const,
    tagline: "",
  },

  /** The link-page form's default Puck document (Form key "links",
   *  wiki/decisions/link-page-form.md): served when a business has no saved
   *  row, and the builder's starting document. Static node id on purpose —
   *  there is exactly one node, and stable ids keep default docs comparable. */
  DEFAULT_LINKS_FORM_DATA = {
    content: [{ props: { id: "links-page", ...LINKS_PAGE_DEFAULT_PROPS }, type: "LinksPage" }],
    root: { props: {} },
    zones: {},
  },

  /**
   * The links page's whole editing surface: its fields ARE the form. The page
   * kind locks structure editing (no insert/drag/delete of siblings), so the
   * pro reorders, renames, removes, and adds rows through this one component.
   */
  LinksPageConfig: ComponentConfig<LinksPageProps> = {
    defaultProps: { ...LINKS_PAGE_DEFAULT_PROPS },
    fields: {
      logoUrl: LinksPageFields.logoUrl,
      tagline: LinksPageFields.tagline,
      links: LinksPageFields.links as any,
      photos: LinksPageFields.photos as any,
      showRating: LinksPageFields.showRating,
      showSocials: LinksPageFields.showSocials,
    },
    label: "Link Page",
    render: ({ id, puck, editMode, ...props }) => {
      // Business data flows through puck.metadata.business (NEVER read window globals here)
      const metadata = puck?.metadata as
          | {
              business?: JobsiteBusinessContext;
              pagePaths?: string[];
            }
          | undefined,

        // Metadata wins when present (editor preview, static build); the props
        // Fallback keeps story/harness renders honest when there is no metadata.
        // The Simple UI canvas renders through plain <Render> (editMode false),
        // So editor-ness comes from the window config there — same resolution
        // EmergencyBand uses. Without it, a row whose data is missing renders
        // Nothing AND explains nothing, which reads as "editing is broken".
        windowConfig = getWindowJobsiteConfig();
      return (
        <LinksPage
          id={id}
          {...props}
          business={metadata?.business ?? props.business}
          pagePaths={metadata?.pagePaths ?? props.pagePaths}
          isEditing={Boolean(windowConfig?.isEditing ?? editMode)}
        />
      );
    },
  };

export { LINKS_PAGE_DEFAULT_PROPS, DEFAULT_LINKS_FORM_DATA, LinksPageConfig };
